import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { FloatingLabel, Form } from "react-bootstrap";
import { Column, Row, Select } from "../../../../components";
import { RootState } from "../../../../shared/store";
import { PolicyTransactionPremiumDto } from "../../../model/PolicyTransactionPremiumDto";
import { updateSelectedPolicyDetails } from "../../../customer-slice/Customer-slice";
import { CommonUtility } from "../../../../Services/CommonUtility";
import LookupService from "../../../../Services/LookupService"; 
import { DEFAULT_GUID } from "../../../../shared/util/constant";

export interface IPolicyTransactionPremiumFormProps {
  transactionPremium: PolicyTransactionPremiumDto;
  polId?: string;
  polEventId?: string;
  setIsFormSaved?: (isSaved: boolean) => void;
  tabIndex?: number;
}

const PolicyTransactionPremiumForm: React.FunctionComponent<IPolicyTransactionPremiumFormProps> = (props) => {
  let tbIndex = props.tabIndex;
  const { transactionPremium, polId, polEventId, setIsFormSaved } = props;
  const dispatch = useDispatch();
  const customerDetail = useSelector((state: RootState) => state.customerSlice);
  const policyDetailKey = CommonUtility.getPolicyDetailKey(polId, polEventId);
  const policyDetails = customerDetail.selectedPolicyDetails?.[policyDetailKey];

  const [premiumData, setPremiumData] = useState<PolicyTransactionPremiumDto>(transactionPremium || {});
  const [isPremiumFocused, setIsPremiumFocused] = useState(false);
  const [premiumInput, setPremiumInput] = useState<string>('');
  const [billedTypes, setBilledTypes] = useState<{ label: string; value: string }[]>([]); // State for billed types dropdown

  useEffect(() => {
    if (policyDetails && policyDetails.policyTransactionPremiums) {
      const premium = policyDetails.policyTransactionPremiums.find((p) => p.id === transactionPremium.id);
      if (premium) {
        setPremiumData(premium);
      }
    }
  }, [transactionPremium, policyDetails]);

  // Fetch billed types using LookupService
  useEffect(() => {
    const loadBilledTypes = async () => {
      try {
        const response = await LookupService.fetchHowBilledTypes(); 
        const formattedBilledTypes = response?.map((item) => ({
          label: item.lookupValue,
          value: item.id,
        })) || [];
        setBilledTypes(formattedBilledTypes);
      } catch (error) {
        console.error("Error loading billed types:", error);
      }
    };

    loadBilledTypes();
  }, []);

  const onFieldChange = (propName: string, value: string | number) => {
    const updatedData = {
      ...premiumData,
      [propName]: value,
    };
    setPremiumData(updatedData);

    if (!policyDetails) {
      console.error("Policy details are undefined.");
      return;
    }

    const updatedPolicyDetails = JSON.parse(JSON.stringify(policyDetails));
    if (updatedPolicyDetails && updatedPolicyDetails.policyTransactionPremiums) {
      updatedPolicyDetails.policyTransactionPremiums = updatedPolicyDetails.policyTransactionPremiums.map((premium) => {
        if (premium.id === transactionPremium.id) {
          return {
            ...premium,
            ...updatedData,
          };
        }
        return premium;
      });

      dispatch(
        updateSelectedPolicyDetails({
          policyId: polId,
          polEventId: polEventId,
          policyDetails: updatedPolicyDetails,
        })
      );
    }

    if (setIsFormSaved) {
      setIsFormSaved(false);
    }
  };

  return (
    <div className="policy-transaction-premium-form">
      <Row className="mb-3">
        <Column lg={6} md={6}>
          <FloatingLabel controlId="premium" label="Premium">
            <Form.Control
              type="text"
              inputMode="decimal"
              name="premium"
              value={
                isPremiumFocused
                  ? premiumInput
                  : premiumData.premium == null
                    ? ""
                    : Number(premiumData.premium).toFixed(2)
              }
              onFocus={() => {
                setPremiumInput(premiumData.premium == null ? "" : premiumData.premium.toString());
                setIsPremiumFocused(true);
              }}
              onBlur={() => setIsPremiumFocused(false)}
              onChange={(e) => {
                const inputValue = e.target.value;
                if (CommonUtility.isValidDecimalWithNegativeUpto2Limit(inputValue)) {
                  setPremiumInput(inputValue);
                  onFieldChange("premium", CommonUtility.parseDecimalOrZero(inputValue));
                }
              }}
              placeholder="Premium"
              tabIndex={tbIndex++}
            />
          </FloatingLabel>
        </Column>
        <Column lg={6} md={6}>
          <FloatingLabel controlId="billedPremium" label="Billed Premium">
            <Form.Control
              type="number"
              name="billedPremium"
              value={premiumData.billedPremium || ""}
              onChange={(e) => onFieldChange("billedPremium", e.target.value ? parseFloat(e.target.value) : 0)}
              placeholder="Billed Premium"
              tabIndex={tbIndex++}
            />
          </FloatingLabel>
        </Column>
      </Row>
      <Row className="mb-3">
        <Column lg={6} md={6}>
          <FloatingLabel controlId="writtenPremium" label="Written Premium">
            <Form.Control
              type="number"
              name="writtenPremium"
              value={premiumData.writtenPremium || ""}
              onChange={(e) => onFieldChange("writtenPremium", e.target.value ? parseFloat(e.target.value) : 0)}
              placeholder="Written Premium"
              tabIndex={tbIndex++}
            />
          </FloatingLabel>
        </Column>
        <Column lg={6} md={6}>
          <FloatingLabel controlId="fullTermPremium" label="Full Term Premium">
            <Form.Control
              type="number"
              name="fullTermPremium"
              value={premiumData.fullTermPremium || ""}
              onChange={(e) => onFieldChange("fullTermPremium", e.target.value ? parseFloat(e.target.value) : 0)}
              placeholder="Full Term Premium"
              tabIndex={tbIndex++}
            />
          </FloatingLabel>
        </Column>
      </Row>
      <Row className="mb-3">
        <Column lg={6} md={6}>
          <Select
            id='howBilled'
            label="How Billed"
            options={billedTypes}
            defaultSelected={billedTypes?.filter(({ value }) =>
                value === premiumData.howBilled
            )}
        onChange={(value) => {
          onFieldChange('howBilled', value.toString() === "" ? DEFAULT_GUID : value.toString());
        }}
            placeholder="Select How Billed"
            multiple={false}
            tabIndex={tbIndex++}
            className='mb-3'
            // isInvalid={formValidation['howBilled']?.isValid === false}
            // invalidWarning={formValidation['howBilled']?.warningText}
        />
        </Column>
      </Row>
    </div>
  );
};

export default PolicyTransactionPremiumForm;